import { Box, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import formatCurrency from 'helper/short-currency';
import { ClientDataType, ClientType } from 'model/client.model';
import { Link } from 'react-router-dom';
import zeplinColor from 'theme/Color';

interface Column {
  id: 'Name' | 'AccountId' | 'category' | 'closedDealAmount' | 'openDealAmount' | 'totalFollowers';
  label: string;
  minWidth?: number;
  align?: 'center';
  format?: (value: number) => string;
}

const columns: readonly Column[] = [
  { id: 'Name', label: 'Name' },
  { id: 'category', label: 'Category', minWidth: 100 },
  { id: 'closedDealAmount', label: 'Close deals', align: 'center' },
  { id: 'openDealAmount', label: 'Open deals', align: 'center' },
];

const ClientRowGrid = ({
  clients,
  category,
}: {
  clients: ClientDataType;
  category: string | null;
}) => {
  const data =
    category === 'all'
      ? clients?.clients
      : clients?.clients.filter((val) => val.category === category);

  return (
    <Paper sx={{ width: '100%', border: 'none', boxShadow: 'none' }}>
      <TableContainer
        sx={{
          overflow: 'scroll',
          height: { xs: 'calc(100vh - 190px)', xl: 'calc(100vh - 250px)' },
        }}
      >
        <Table stickyHeader aria-label='sticky table'>
          <TableHead>
            <TableRow
              sx={{
                border: '1px solid black',
                borderRadius: '14px',
                '& th': {
                  backgroundColor: '#f5f5f5',
                },
                '& th:first-child': {
                  borderRadius: '14px 0px 0px 14px',
                },
                '& th:last-child': {
                  borderRadius: '0px 14px 14px 0px',
                },
              }}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, ind) => {
              return (
                <TableRow
                  hover
                  role='checkbox'
                  tabIndex={-1}
                  key={ind}
                  sx={{
                    '&:last-child td, &:last-child th': { borderBottom: '2px solid #ddd' },
                  }}
                >
                  {columns.map((column) => {
                    const value =
                      column.id === 'closedDealAmount' || column.id === 'openDealAmount'
                        ? `$${formatCurrency(+(row[column.id] || 0))}`
                        : row[column.id as keyof ClientType];
                    return (
                      <TableCell key={column.id} align={column.align}>
                        <Link
                          to={`huddle/${row.AccountId}`}
                          state={{ client: row }}
                          style={{ textDecoration: 'none', color: zeplinColor.BasicBlack }}
                        >
                          {column.id === 'Name' ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                cursor: 'pointer',
                              }}
                            >
                              {row?.avatar ? (
                                <img
                                  width={'60px'}
                                  height={'60px'}
                                  src={row?.avatar}
                                  alt='logo'
                                  style={{ objectFit: 'cover', borderRadius: '14px' }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: '60px',
                                    height: '60px',
                                    borderRadius: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor:
                                      row?.category === 'Talent'
                                        ? zeplinColor.Brand1
                                        : row?.category === 'Athlete'
                                          ? zeplinColor.USER_PROFILE_COLOR1
                                          : row?.category === 'Business'
                                            ? zeplinColor.USER_PROFILE_COLOR2
                                            : row?.category === 'Business_Development'
                                              ? zeplinColor.USER_PROFILE_COLOR3
                                              : '',
                                  }}
                                >
                                  <Typography
                                    variant='display5'
                                    sx={{ color: zeplinColor.Neutral1 }}
                                  >
                                    {value
                                      ?.split(' ')
                                      .map((val, index) =>
                                        index === 0 || index === value?.split(' ').length - 1
                                          ? val[0]
                                          : '',
                                      )}
                                  </Typography>
                                </Box>
                              )}
                              {value ?? 0}
                            </Box>
                          ) : column.format && typeof value === 'number' ? (
                            column.format(value)
                          ) : (
                            value
                          )}
                        </Link>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ClientRowGrid;
